import Script from "next/script";
import React from "react";
const IUBENDA_SITE_ID = process.env.NEXT_PUBLIC_IUBENDA_SITE_ID;

// Legal-approved banner copy (Sept 2026, CCPA/GDPR update). Iubenda replaces
// `%{cookie_policy_link}` with a link captioned by `cookiePolicyLinkCaption`.
// Button captions live in the banner config below, so the dashboard texts no
// longer apply to them.
const BANNER_CONTENT = "We use strictly necessary and additional cookies to provide you with the best online experience. " + "Read our %{cookie_policy_link}.";
const defaultState = {
  necessary: false,
  functionality: false,
  experience: false,
  measurement: false,
  marketing: false,
  isReady: false
};
export function useIubendaPreferences() {
  const [state, setState] = React.useState(defaultState);
  React.useEffect(() => {
    const handleIubendaReady = (event: CustomEvent) => {
      const {
        preferences,
        notNeeded
      } = event.detail;
      // e.g US
      if (notNeeded) {
        setState({
          necessary: true,
          functionality: true,
          experience: true,
          measurement: true,
          marketing: true,
          isReady: true
        });
        return;
      }
      const necessary = preferences?.purposes?.["1"] === true;
      const functionality = preferences?.purposes?.["2"] === true;
      const experience = preferences?.purposes?.["3"] === true;
      const measurement = preferences?.purposes?.["4"] === true;
      const marketing = preferences?.purposes?.["5"] === true;
      const result = {
        necessary,
        functionality,
        experience,
        measurement,
        marketing,
        isReady: true
      };
      setState(result);
    };
    window.addEventListener("iubenda_ready", handleIubendaReady as EventListener);
    return () => {
      window.removeEventListener("iubenda_ready", handleIubendaReady as EventListener);
    };
  }, []);
  return state;
}
export function IubendaScript() {
  if (!IUBENDA_SITE_ID) {
    return null;
  }
  return <>
      <Script id="iubenda" dangerouslySetInnerHTML={{
      __html: `
            var _iub = _iub || [];
            var iubendaReady = false;
            var preferences = null;

            _iub.csConfiguration = {
              "applyGdprForCH": true,
              "askConsentAtCookiePolicyUpdate": true,
              "countryDetection": true,
              "enableFadp": true,
              "enableLgpd": true,
              // TCF off: with the IAB framework on, Iubenda ignores
              // banner.content and forces its long TCF notice, which the
              // legal-approved copy replaces. The TCF stub scripts went too.
              "enableTcf": false,
              "enableUspr": true,
              "floatingPreferencesButtonDisplay": "bottom-right",
              "googleConsentMode": true,
              "gdprAppliesGlobally": true,
              "lang": "en",
              "lgpdAppliesGlobally": false,
              "perPurposeConsent": true,
              "siteId": ${IUBENDA_SITE_ID},
              "storage": { "useSiteId": true },
              "whitelabel": false,
              "cookiePolicyId": 48799615,
              "banner": {
                "applyStyles": false,
                "acceptButtonDisplay": true,
                "acceptButtonCaption": "Accept All",
                "closeButtonDisplay": false,
                "content": ${JSON.stringify(BANNER_CONTENT)},
                "cookiePolicyLinkCaption": "Cookie Policy",
                "customizeButtonDisplay": true,
                "customizeButtonCaption": "Go to my cookie choices",
                "explicitWithdrawal": false,
                "listPurposes": false,
                "ownerName": "ouster.com",
                "privacyPolicyUrl": "https://ouster.com/privacy-policy/",
                "position": "float-bottom-center",
                "rejectButtonDisplay": true,
                "rejectButtonCaption": "Reject All",
                "showPurposesToggles": false,
                "showTotalNumberOfProviders": false
              },
              "callback": {
                onReady: function() {
                  iubendaReady = true;
                  const preferences = _iub.cs.api.getPreferences();

                  const event = new CustomEvent("iubenda_ready", {
                    detail: { preferences },
                  });

                  window.dispatchEvent(event);
                },
                onPreferenceNotNeeded: function(prefs) {
                  const event = new CustomEvent("iubenda_ready", {
                    detail: { prefs, notNeeded: true },
                  });
                  window.dispatchEvent(event);
                },
                onPreferenceExpressed: function() {
                  // Re-fire the ready event so consumers (GTM, Warmly,
                  // ExternalVideo, etc.) pick up the new preferences
                  // without a full page reload.
                  if (iubendaReady) {
                    const preferences = _iub.cs.api.getPreferences();
                    const event = new CustomEvent("iubenda_ready", {
                      detail: { preferences },
                    });
                    window.dispatchEvent(event);
                  }
                }
              }
            };
          `
    }} />
      <Script src="//cdn.iubenda.com/cs/gpp/stub.js" strategy="afterInteractive" />
      <Script src="//cdn.iubenda.com/cs/iubenda_cs.js" strategy="afterInteractive" />
    </>;
}